import {
    IllegalArgumentException,
    JsonParseException,
    maybeNum,
    maybeStr,
    MsgException, nonemptyArray, nonEmptyString,
    noNullApply,
    numOrNull, nvl,
    requireNum,
    requireStr
} from "./utils";
import {TerminalWarrantyYears, UnimportantColor} from "./constants";

// Copied from google.maps.d.t since we could not manage importing it properly
export type LatLngLiteral = { lat: number; lng: number }
export interface CircleOptions { // Just some of the options for now, add more if needed
    /** Indicates whether this Circle handles mouse events. Defaults to true. */
    clickable?: boolean;
    /**
     * The stroke color. All CSS3 colors are supported except for extended
     * named colors.
     */
    strokeColor?: string;
    /** The stroke opacity between 0.0 and 1.0 */
    strokeOpacity?: number;
    /** The stroke width in pixels. */
    strokeWeight?: number;

    fillColor?: string;
    fillOpacity?: number; // 0.0 -> 1.0
}
export interface PolygonOptions {
    /**
     * Indicates whether this Polygon handles mouse events. Defaults to true.
     */
    clickable?: boolean;
    /**
     * The fill color. All CSS3 colors are supported except for extended named
     * colors.
     */
    fillColor?: string;
    /** The fill opacity between 0.0 and 1.0 */
    strokeColor?: string;
    /** The stroke opacity between 0.0 and 1.0 */
    strokeOpacity?: number;
    /** The stroke width in pixels. */
    strokeWeight?: number;
    /** Whether this polygon is visible on the map. Defaults to true. */
    visible?: boolean;
    /** The zIndex compared to other polys. */
    zIndex?: number;
}
export interface LatLng {
    /**
     * Creates a LatLng object representing a geographic point.
     * Note the ordering of latitude and longitude.
     * @param lat Latitude is specified in degrees within the range [-90, 90].
     * @param lng Longitude is specified in degrees within the range [-180, 180].
     * @param noWrap Set noWrap to true to enable values outside of this range.
     */
    constructor(lat: number, lng: number, noWrap?: boolean): LatLng;
    /** Comparison function. */
    equals(other: LatLng): boolean;
    /** Returns the latitude in degrees. */
    lat(): number;
    /** Returns the longitude in degrees. */
    lng(): number;
    /** Converts to string representation. */
    toString(): string;
    /** Returns a string of the form "lat,lng". We round the lat/lng values to 6 decimal places by default. */
    toUrlValue(precision?: number): string;
    /** Converts to JSON representation. This function is intended to be used via JSON.stringify. */
    toJSON(): LatLngLiteral;
}
// End copied from google.maps.d.ts


export class TerminalLocation {
    private _id: number;
    public description: string;
    private _companyOrgNum: number;
    public unverifiedRegsOk: boolean;
    public shutdownTime: DateTime | null;
    public address: string | null;
    public constructorName: string | null;
    private _invoiceRef: string | null;
    private _regWithoutTerminal: boolean;
    public mobileRegGeoRequired: boolean;
    public mobileRegRegcodeRequired: boolean;
    public requirePhoneAuth: boolean;
    public hseGuidelineEnabled: number;
    public mobileRegOutGeoRequired: boolean;
    public allowRegOutMobile: boolean;


    constructor(id: number, description: string, companyOrgNum: number, unverifiedRegsOk: boolean, shutdownTime: DateTime | null
                , address: string | null, constructorName: string | null, invoiceRef: string | null, regWithoutTerminal: boolean,
                mobileRegGeoRequired: boolean, mobileRegRegcodeRequired: boolean, requirePhoneAuth: boolean, hseGuidelineEnabled: number,
                mobileRegOutGeoRequired: boolean, allowRegOutMobile :boolean) {
      this._id = id;
      this.description = description;
      this._companyOrgNum = companyOrgNum;
      this.unverifiedRegsOk = unverifiedRegsOk;
      this.shutdownTime = shutdownTime;
      this.address = address;
      this.constructorName = constructorName;
      this._invoiceRef = invoiceRef;
      this._regWithoutTerminal = regWithoutTerminal;
      this.mobileRegGeoRequired = mobileRegGeoRequired;
      this.mobileRegRegcodeRequired = mobileRegRegcodeRequired;
      this.requirePhoneAuth = requirePhoneAuth;
      this.hseGuidelineEnabled = hseGuidelineEnabled;
      this.mobileRegOutGeoRequired = mobileRegOutGeoRequired;
      this.allowRegOutMobile = allowRegOutMobile;
    }

    get id(): number {
      return this._id;
    }
    get companyOrgNum(): number {
      return this._companyOrgNum;
    }
    get isShutdown(): boolean{
        return this.shutdownTime != null;
    }
    set invoiceRef(ir: string | null) {
        this._invoiceRef = ir;
    }
    get invoiceRef(): string | null {
        return this._invoiceRef;
    }
    get regWithoutTerminal(): boolean {
        return this._regWithoutTerminal;
    }
    set regWithoutTerminal(value: boolean) {
        this._regWithoutTerminal = value;
    }

    static fromJson(json: any): TerminalLocation {
        try {
            let id: number = requireNum(json.id);
            let description: string = requireStr(json.description);
            let orgNum: number = requireNum(json.companyOrgNum);
            let unvRegOk: boolean = json.unverifiedRegsOk === true;
            let shutdownTime: DateTime = noNullApply( maybeStr(json.shutdownTime), DateTime.fromString);
            let address: string = maybeStr(json.address);
            let constructorName: string = maybeStr(json.constructorName);
            let invoiceRef: string = maybeStr(json.invoiceRef);
            let regWithoutTerminal = json.regWithoutTerminal === true;
            let mobileRegGeoRequired = json.mobileRegGeoRequired === true;
            let mobileRegRegcodeRequired = json.mobileRegRegcodeRequired === true;
            let hseGuidelineEnabled = numOrNull(json.hseGuidelineEnabled);
            if(hseGuidelineEnabled == null) {
                console.warn(`TerminalLocation.fromJson, hseGuidelineEnabled property not found`);
                hseGuidelineEnabled = 0;
            }
            return new TerminalLocation(
                id, description, orgNum, unvRegOk, shutdownTime, address, constructorName, invoiceRef, regWithoutTerminal,
                mobileRegGeoRequired, mobileRegRegcodeRequired, json.requirePhoneAuth === true, hseGuidelineEnabled,
                json.mobileRegOutGeoRequired, json.allowRegOutMobile
            );
        } catch (e) {
            if(e instanceof MsgException) throw new JsonParseException("TerminalLocation", e);
            else throw e;
        }
    }

    toJson(): string {
        return JSON.stringify({
            "id": this.id,
            "description": this.description,
            "companyOrgNum": this.companyOrgNum,
            "unverifiedRegsOk": this.unverifiedRegsOk,
            "shutdownTime": this.shutdownTime != null ? this.shutdownTime.toJSON() : null,
            "address": this.address,
            "constructorName": this.constructorName,
            "invoiceRef": this.invoiceRef,
            "regWithoutTerminal": this.regWithoutTerminal,
            "mobileRegGeoRequired": this.mobileRegGeoRequired,
            "mobileRegRegcodeRequired": this.mobileRegRegcodeRequired,
            "requirePhoneAuth": this.requirePhoneAuth,
            "hseGuidelineEnabled": this.hseGuidelineEnabled,
            "mobileRegOutGeoRequired": this.mobileRegOutGeoRequired,
            "allowRegOutMobile": this.allowRegOutMobile,
        });
    }
}


export class Company  {
  public name: string;
  public orgNum: number;
  constructor(name: string, orgNum: number) {
      this.name = name;
      this.orgNum = orgNum;
  }

  static fromJson(json: any): Company {
      let name = requireStr(json.name);
      let orgnum = requireNum(json.orgNum);
      return new Company(name, orgnum);
  }

  get nameAndOrgnum() {
      return `${this.name} (${this.orgNum})`;
  }

}

export class CompanyConfig {
    constructor(public regReportEnabled: boolean, public requirePhoneAuthEnabled: boolean) {}

    static default() {
        return new CompanyConfig(false, false);
    }

    static fromJson(json: any) {
        return new CompanyConfig(json.regReportEnabled === true, json.requirePhoneAuthEnabled === true);
    }
}

// Company info where name or orgNum might be missing - not verified
export class UnverifiedCompany {
    constructor(public name: string | null, public orgNum: number | null) {}
    // When we want to get unverified company info entity (unverified regs)
    static fromJson(json: any): UnverifiedCompany {
        let name = maybeStr(json.name);
        let orgnum = numOrNull(json.orgNum);
        return new UnverifiedCompany(name, orgnum);
    }
}

export class DateTime {
    static readonly minmillis = -1733014800000;
    private _date: Date;
    static fromDayParamRegex: RegExp = /([0-9]{4})-([0-9]{1,2})-([0-9]{1,2})/;
    static fromNorwStyleRegex: RegExp = /([0-9]{1,2})\.([0-9]{1,2})\.([0-9]{4})$/;
    static minDate = new DateTime(new Date(DateTime.minmillis));

    constructor(date: Date) {
        this._date = date;
    }

    static isValidDate(d: any): boolean {
        return d instanceof Date && d.getTime() == d.getTime(); // NaN check
    }

    static fromString(dateStr: string): DateTime {
        let d: Date = new Date(Date.parse(dateStr));
        if(DateTime.isValidDate(d)) {
            return new DateTime(d);
        }
        return DateTime.fromNorwStyle(dateStr);
    }

    static fromTimestamp(ts: number): DateTime {
        return new DateTime(new Date(ts));
    }

    static now(): DateTime {
        return new DateTime(new Date());
    }

    static fromNorwStyle(dateStr: string): DateTime {
        let result = DateTime.fromNorwStyleRegex.exec(dateStr);
        if(result != null) {
            let day = requireNum(result[1]);
            let month = requireNum(result[2]) - 1;
            let year = requireNum(result[3]);
            return new DateTime(new Date(year, month, day));
        } else {
            throw new Error(`Invalid date string: ${dateStr}`);
        }
    }

    static fromDayParam(dayParam: string): DateTime {
        let result = DateTime.fromDayParamRegex.exec(dayParam);
        if(result != null) {
            let year = requireNum(result[1]);
            let month = requireNum(result[2]) - 1;
            let day = requireNum(result[3]);
            return new DateTime(new Date(year, month, day));
        } else {
            throw new Error(`Invalid day param: ${dayParam}`);
        }
    }
    static fromNorwStyleNoDots(dateStr: string): DateTime {
        if(dateStr.length == 8) {
            const d = dateStr.substr(0, 2);
            const m = dateStr.substr(2, 2);
            const y = dateStr.substr(4, 4);
            return DateTime.fromNorwStyle(`${d}.${m}.${y}`);
        }
        throw new Error(`invalid date input: ${dateStr}`);

    }

    static maybeFromJson(maybeDt: any): DateTime | null {
        return noNullApply(maybeStr(maybeDt), DateTime.fromString);
    }

    static fromJson(dt: any): DateTime {
        return DateTime.fromString(requireStr(dt));
    }

    private static numTwoChars(num: number): string {
        return num < 10 ? "0"+num.toString() : num.toString();
    }

    toLocalDate(): string {
        let str = DateTime.numTwoChars(this._date.getDate())+"."+DateTime.numTwoChars(this._date.getMonth()+1) +"."+this._date.getFullYear();
        //return this._date.toLocaleDateString();
        return str;
    }

    toLocalTime(): string {
        let str = DateTime.numTwoChars(this._date.getHours())+":"+DateTime.numTwoChars(this._date.getMinutes());
        //return this._date.toLocaleTimeString();
        return str;
    }

    toLocalDateTime(): string {
        //return this._date.toLocaleString();
        return this.toLocalDate()+" "+this.toLocalTime();
    }
    // return date time value in ISO8601 format with timezone offset
    toISOWithOffset(): string {
        const y = String( this._date.getFullYear() );
        const mon = DateTime.numTwoChars( this._date.getMonth() + 1 );
        const d = DateTime.numTwoChars( this._date.getDate() );
        const h = DateTime.numTwoChars( this._date.getHours() );
        const min = DateTime.numTwoChars( this._date.getMinutes() );
        const s = DateTime.numTwoChars( this._date.getSeconds() );
        let ms = String(this._date.getMilliseconds());
        if(ms.length == 1) ms = "00"+ms;
        else if(ms.length == 2) ms = "0"+ms;
        const offset = DateTime.numTwoChars(Math.floor(this._date.getTimezoneOffset() / 60 )); // only do whole hour offsets for now
        return `${y}-${mon}-${d}T${h}:${min}:${s}.${ms}`;
    }

    toMidnight(): DateTime {
        let year = this._date.getFullYear();
        let month = this._date.getMonth();
        let day = this._date.getDate();
        return new DateTime(new Date(year, month, day))
    }

    clone(): DateTime {
        return new DateTime(new Date(this._date.getTime()));
    }

    nextDay(): DateTime {
        let newDate = new Date(this._date.getTime());
        newDate.setDate(newDate.getDate() + 1);
        return new DateTime( newDate );
    }

    dayOffset(offset: number): DateTime {
        let newDate = new Date(this._date.getTime());
        newDate.setDate(newDate.getDate() + offset);
        return new DateTime( newDate );
    }

    prevDay(): DateTime {
        let newDate = new Date(this._date.getTime());
        newDate.setDate(newDate.getDate() - 1);
        return new DateTime( newDate );
    }

    plusMillis(millis: number): DateTime {
        let newDate = new Date(this._date.getTime());
        newDate.setMilliseconds(newDate.getMilliseconds() + millis);
        return new DateTime(newDate);
    }

    toJSON(): string {
        return this._date.toJSON()
    }
    toString(): string {
        return this.toISOWithOffset();
    }

    toLocalJsonDate(): string {
        return this.toDayParam();
    }

    toJsDate(): Date {
        return this._date;
    }

    /** Suitable to send day value to server as param (and for putting on the location bar as hash fragment */
    toDayParam(): string {
        let month: number = this._date.getMonth() + 1;
        return this._date.getFullYear()+"-"+DateTime.numTwoChars(month)+"-"+DateTime.numTwoChars(this._date.getDate());
    }

    dayOfMonth(): number {
        return this._date.getDate();
    }

    monthOfYear(): number {
        return this._date.getMonth() + 1;
    }

    year(): number {
        return this._date.getFullYear();
    }

    /** Return the number of milliseconds since epoch */
    millis(): number {
        return this._date.getTime();
    }

    equals(other: any): boolean {
        if(other == null) return false;
        if(!(other instanceof DateTime)) return false;
        let otherDateTime: DateTime = <DateTime> other;
        return this.millis() == otherDateTime.millis();
    }

    isToday(): boolean {
        let tod = new DateTime(new Date());
        return this.year() == tod.year() && this.monthOfYear() == tod.monthOfYear() && this.dayOfMonth() == tod.dayOfMonth();
    }

    isBefore(other: DateTime): boolean {
        return other == null || this.millis() < other.millis();
    }
    isAfter(other: DateTime): boolean {
        return other == null || this.millis() > other.millis();
    }
    monthOffset(n: number): DateTime {
        let newDate = new Date(this._date.getTime());
        newDate.setMonth(newDate.getMonth() + n);
        // Check if setMonth went more than one month ahead. Happens if the current day is higher than number of days in next month
        // (E.g. On 31.10, it will skip to 01.12 because november has only 30 days.)
        // This also works going backwards with negative offset from e.g. april 30th to february. Will then end up on february 28th or 29th.
        if(newDate.getMonth() > this._date.getMonth() + n) {
            // Go to last day of previous month so that we don't skip it
            newDate.setFullYear(newDate.getFullYear(), newDate.getMonth(), 0);
        }
        return new DateTime( newDate );
    }
    monthName(): string {
        const df = new Intl.DateTimeFormat("no", {month: "long"});
        return df.format(this._date);
    }
    private setYear(y: number) {
        this._date.setFullYear(y);
    }
    withYear(year: number): DateTime {
        const n = this.clone();
        n.setYear(year);
        return n;
    }
    private setDay(day: number) {
        this._date.setDate(day);
    }
    withDay(day: number): DateTime {
        const n = this.clone();
        n.setDay(day);
        return n;
    }
}



/*

 class UnverifiedPerson {
 constructor(public guid: string,public  firstName: string,public  lastName: string,public  companyName: string,public phone: string) {}
 }

 class SiteInOut {
 constructor(public guid: string, public terminalLocationId: number, public timeIn: string, public timeOut: string,
 public adjustedIn: string, public adjustedOut: string, public person: Person, public unverifiedPerson: UnverifiedPerson) {}
 }
 */

export class Terminal{
    constructor(public guid: string, public description: string, public locationId: number, public companyOrgNum: number, public appVersion: number,
                public deactivated: DateTime | null, public phoneDeviceid: string | null, public simSerial: string | null, public licenseType: number = 0,
                public cleanupTs: DateTime | null, public lastSyncTs: DateTime, public theftAlarmState: number, public grpcSubscribeTime: DateTime | null,
                public geolocation: JLocation | null, public createdTs: DateTime, public technicalCheckDoneTs: DateTime | null, public continuousPushlistener: boolean,
                public androidSdkVersion: number) {}

    static fromJson(json: any): Terminal {
        let guid = requireStr(json.guid);
        let description = requireStr(json.description);
        let locationId = maybeNum(json.locationId);
        let companyOrgNum = requireNum(json.companyOrgNum);
        let appVersion = requireNum(json.appVersion);
        let deactivated = DateTime.maybeFromJson(json.deactivated);
        let phoneDeviceid = maybeStr(json.phoneDeviceid);
        let simSerial = maybeStr(json.simSerial);
        let licenseType = maybeNum(json.licenseType);
        if(licenseType == null) {
            licenseType = 0;
        }
        let cleanupTs = DateTime.maybeFromJson(json.cleanupTs);
        let lastSyncTs = DateTime.fromJson(json.lastSyncTs);
        let theftAlarmState = requireNum(json.theftAlarmState);
        let grpcSubscribeTime = DateTime.maybeFromJson(json.grpcSubscribeTime);
        let geolocation = json.geolocation ? JLocation.fromJson(json.geolocation) : null;
        let createdTs = DateTime.fromJson(json.createdTs);
        let checkTs = DateTime.maybeFromJson(json.technicalCheckDoneTs);
        let contPush = json.continuousPushlistener === true;
        const androidSdkVersion = requireNum(json.androidSdkVersion || 0);

        return new Terminal(guid, description, locationId, companyOrgNum, appVersion, deactivated, phoneDeviceid,
            simSerial, licenseType, cleanupTs, lastSyncTs, theftAlarmState, grpcSubscribeTime, geolocation, createdTs, checkTs, contPush, androidSdkVersion);
    }

    get technicalCheckDue(): boolean {
        const now = DateTime.now();
        const threeYearsAgo = now.withYear(now.year() - 3);
        if(this.createdTs.isBefore(threeYearsAgo)) {
            const sixMonthsAgo = now.monthOffset(-6);
            return this.technicalCheckDoneTs == null || this.technicalCheckDoneTs.isBefore(sixMonthsAgo);
        } else {
            return false;
        }
    }

    get warrantyExpired(): boolean {
        const now = DateTime.now();
        const oneYearAgo = now.withYear(now.year() - TerminalWarrantyYears);
        return this.createdTs.isBefore(oneYearAgo);
    }
}

export class TerminalGuiInfo {
    constructor(public terminal: Terminal, public location: TerminalLocation | null, public nextConfig: TerminalConfigNext | null) {}

    static fromJson(json: any): TerminalGuiInfo {
        let terminal = Terminal.fromJson(json.terminal);
        let loc = json.location != null ? TerminalLocation.fromJson(json.location): null;
        let nconf = json.nextConfig != null ? TerminalConfigNext.fromJson(json.nextConfig) : null;

        return new TerminalGuiInfo(terminal, loc, nconf);
    }
}



export class Person {
 constructor(public guid: string, public firstName: string | null, public lastName: string | null, public birthDate: DateTime | null,
             public tmcuser: string | null, public companyOrgnum: number | null, public companyName: string | null) {}

 static fromJson(json: any): Person | null {
     if(json == null || typeof json == "undefined") return null;

     let guid = requireStr(json.guid);
     let firstName = maybeStr(json.firstName);
     let lastName = maybeStr(json.lastName);
     let birthDate = noNullApply(maybeStr(json.birthDate), DateTime.fromString);
     let tmcuser = maybeStr(json.tmcuser);
     let companyName = maybeStr(json.companyName);
     let companyOrgnum = numOrNull(json.companyOrgnum);

     return new Person(guid, firstName, lastName, birthDate, tmcuser, companyOrgnum, companyName);
 }

 get name(): string {
     return ((this.firstName != null ? this.firstName : "") + " " + (this.lastName != null ? this.lastName : "")).trim();
 }
}

export class IdCard {
    constructor (public firstName: string, public lastName: string, public gender: string,
        public birthDate: DateTime, public orgNum: number, public companyName: string, public cardNum: number, public expirationDate: DateTime,
        public personRef: string, public firstReg: DateTime, public deactivated: DateTime | null) {}

    static fromJson(json: any): IdCard {
        let fname = requireStr(json.firstName);
        let lname = requireStr(json.lastName);
        let gender = requireStr(json.gender);
        let birthDate = DateTime.fromString( requireStr(json.birthDate) );
        let orgNum = requireNum(json.orgNum);
        let companyName = requireStr(json.companyName);
        let cardNum = requireNum(json.cardNum);
        let expirationDate = DateTime.fromString(requireStr(json.expirationDate));
        let personRef = maybeStr(json.personRef);
        let firstReg: DateTime = DateTime.fromString(requireStr(json.firstReg));
        let deactivated: DateTime | null = noNullApply( maybeStr(json.deactivated), DateTime.fromString);

        return new IdCard(fname, lname, gender, birthDate, orgNum, companyName, cardNum, expirationDate, personRef, firstReg, deactivated);
    }

    get name() {
        return ((this.firstName != null ? this.firstName : "") + " " + (this.lastName != null ? this.lastName : "")).trim();
    }

    get company(): Company {
        return new Company(this.companyName, this.orgNum);
    }

    isSoonExpired(compareDate: DateTime = DateTime.now()) {
        return IdCard.isSoonExpired(this.expirationDate, compareDate);
    }
    isExpired(compareDate: DateTime = DateTime.now()) {
        return IdCard.isExpired(this.expirationDate, compareDate);
    }
    isDeactivated() {
        return this.deactivated != null && this.deactivated.isBefore(DateTime.now());
    }

    static isSoonExpired(expirationDate: DateTime, compareDate: DateTime = DateTime.now()): boolean {
        const soon = compareDate.plusMillis(1000*60*60*24*30 /* approximately 1 month */);
        return expirationDate.isBefore(soon);
    }
    static isExpired(expirationDate: DateTime, compareDate: DateTime = DateTime.now()): boolean {
        return expirationDate.isBefore(compareDate);
    }
}

export class InvalidPhoneNumber extends Error {
    constructor(msg: string) {
        super(msg);
    }
}

export class PhoneNumber {
    constructor(public countrycode: number, public number: number) {}

    equals(other: PhoneNumber | null): boolean {
        return other != null && other.countrycode == this.countrycode && other.number == this.number;
    }

    static fromJson(json: any): PhoneNumber {
        let c = requireNum(json.countrycode);
        let n = requireNum(json.number);
        return new PhoneNumber(c,n);
    }

    static fromString(phoneStr: string): PhoneNumber {
        try {
            let c = 47;
            let n = 0;
            const str = phoneStr.trim().replace(/[^+0123456789]/g, "");
            if (str.charAt(0) == "+") {
                c = requireNum(str.substr(1, 2));
                n = requireNum(str.substr(3));
            } else {
                n = requireNum(str);
            }
            return new PhoneNumber(c, n);
        } catch (e) {
            throw new InvalidPhoneNumber(`${phoneStr} cannot be parsed into a valid PhoneNumber`);
        }
    }

    public toString() {
        return "+"+this.countrycode.toString()+" "+this.number.toString();
    }

    public splitformat() {
        let str = this.number.toString();
        if (str.length === 8) {
            const parts = this.number.toString().split("");
            str = `${str[0]}${str[1]} ${str[2]}${str[3]} ${str[4]}${str[5]} ${str[6]}${str[7]}`;
        }
        return `+${this.countrycode} ${str}`;
    }
}

export enum companyroleid {
    admin = 1,
    reporter  = 2,
    terminals = 6,
    super = 1000,
}

export enum locationroleid {
    admin = 3,
    reporter = 4,
}

export class Role {
    public static readonly COMPANY_ADMIN = 1;
    public static readonly COMPANY_SUPER = 1000;
    public static readonly LOCATION_ADMIN = 3;

    constructor(public id: number, public name: string) {}

    static fromJson(json: any): Role {
        return new Role(json.id, json.name);
    }

    static descriptions: {[index:number]: string} = {
        1: "Firmaadministrator",
        2: "Firmarapportering",
        3: "Anleggsadministrator",
        4: "Anleggsrapportering",
        6: "Firmaanlegg og kortleseroppsett"
    };

    static typenames: {[index:number]: string} = {
        1: "Administrator",
        2: "Rapportering",
        3: "Administrator",
        4: "Rapportering",
        6: "Anlegg og kortleseroppsett",
        1000: "Super"
    };

    get typename(): string {
        return Role.typenames[this.id] || this.name ;
    }

    get description(): string {
        return Role.descriptions[this.id] || this.name;
    }
}

export class RolesForLocation {
    constructor(public location: TerminalLocation, public roles: Role[]) {}

    static fromJson(json: any): RolesForLocation {
        return new RolesForLocation(TerminalLocation.fromJson(json.location), json.roles.map(Role.fromJson));
    }
}

export class RolesForCompany {
    constructor(public company: Company, public companyRoles: Role[], public locationRoles: RolesForLocation[]) {}

    static fromJson(json: any): RolesForCompany {
        let c = Company.fromJson(json.company);
        let cr: Role[] = [];
        if(json.companyRoles instanceof Array) {
            for (let i = 0; i < json.companyRoles.length; i++) {
                cr[i] = Role.fromJson(json.companyRoles[i]);
            }
        }
        let rfl: RolesForLocation[] = [];
        if(json.locationRoles instanceof Array) {
            for (let j = 0; j < json.locationRoles.length; j++) {
                rfl[j] = RolesForLocation.fromJson(json.locationRoles[j]);
            }
        }
        return new RolesForCompany(c, cr, rfl);
    }
}


export class TmcPersonLink {
    //  TmcPersonLink(personRef: UUID, companyOrgNum: Long, tmcDbName: String, tmcEmployeeNum: Int)

    constructor(public personRef: string, public companyOrgNum: number, public tmcDbName: string, public tmcEmployeeNum: number) {}

    static fromJson(json: any): TmcPersonLink {
        let personRef = requireStr(json.personRef);
        let orgNum = requireNum(json.companyOrgNum);
        let dbName = requireStr(json.tmcDbName);
        let empNum = requireNum(json.tmcEmployeeNum);

        return new TmcPersonLink(personRef, orgNum, dbName, empNum);
    }
}

export const relationTypes = {
    unverifiedEmployedBy: "unverified-employed-by",
    unverifiedWorkingFor: "unverified-working-for",
};

export class EmailAddress {
    constructor(public id: number, public address: string, public since: DateTime) {}

    static fromJson(json: any): EmailAddress | null {
        if(json) {
            const since = DateTime.fromJson(json.since)
            return new EmailAddress(requireNum(json.id), requireStr(json.address), since)
        } else {
            return null;
        }
    }
}

export class CardUserdata {
    constructor(public person: Person, public phoneNumbers: PhoneNumber[], public cards: IdCard[],
        public tmcids: TmcPersonLink[], public onlyOneCardCompany: boolean,
        public roles: RolesForCompany[], public pendingPhoneNumbers: PhoneNumber[],
        public companyRelations: PersonCompanyRelation[], public personmergeTime: DateTime | null,
        public emailAddresses: EmailAddress[], public pendingEmailAddresses: EmailAddress[]) {}

    isVerified() {
        return this.cards.length > 0;
    }
    phoneNumbersJoined(): string {
        return this.phoneNumbers.map((p) => p.toString()).join(", ");
    }

    // Returns true if user has company role with any of given type id
    hasAnyCompanyRoleType(roleTypeIds: companyroleid[], orgnum: number) {
         if(!orgnum || orgnum < 10000000) {
            return false;
        }
        const i = this.roles.findIndex((rfc: RolesForCompany) => {
            if(rfc.company.orgNum === orgnum) {
              const roleIdx = rfc.companyRoles.findIndex((r: Role) => roleTypeIds.some(id => r.id === id));
              return roleIdx > -1;
            } else {
                return false;
            }
        });

        return i > -1;
    }

    hasAnyLocationRoleType(roleTypeIds: locationroleid[], orgnum: number, locationId: number) {
        for(let rfc of this.roles) {
            if(rfc.company.orgNum === orgnum) {
                for(let rfl of rfc.locationRoles) {
                    if(rfl.location.id === locationId) {
                        for(let r of rfl.roles) {
                            if(roleTypeIds.some(roleId => r.id === roleId)) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    usersCompanies() {
        // First extract companies the user has roles on
        return this.roles.map((rfc: RolesForCompany) => rfc.company).filter((c: Company, idx: number, self: Company[]) => self.indexOf(c) === idx);
    }

    isCompanyAdmin(orgnum: number) {
        return this.hasAnyCompanyRoleType([companyroleid.admin, companyroleid.super], orgnum);
    }

    isCompanySuper(orgnum: number) {
        return this.hasAnyCompanyRoleType([companyroleid.super], orgnum);
    }

    isCompanyTerminalSetup(orgnum: number) {
        return this.hasAnyCompanyRoleType([companyroleid.terminals, companyroleid.super], orgnum);
    }

    isLocationAdmin(orgnum: number, locationId: number) {
        return this.hasAnyLocationRoleType([locationroleid.admin], orgnum, locationId);
    }

    get employedAt(): Company | null {
        const now = DateTime.now().plusMillis(100);
        const r = this.companyRelations.filter((r) => {
            return r.relationType == relationTypes.unverifiedEmployedBy &&
                r.fromTs.isBefore(now) &&
                (r.toTs == null || !r.toTs.isBefore(now));
        });
        if(r.length == 0) {
            return null;
        } else if(r.length == 1) {
            return r[0].company;
        }
        throw new Error("Multiple employed at companies not supported yet");
    }

    static fromJson(json: any): CardUserdata | null {
        let person = Person.fromJson(json.person);
        let phoneNumbers = json.phoneNumbers.map((pjson: any) => { return PhoneNumber.fromJson(pjson); });
        let cards = json.cards.map((cjson: any) => { return IdCard.fromJson( cjson ); });
        let tmcids = json.tmcids.map((tjson: any) => { return TmcPersonLink.fromJson(tjson); });
        let onlyOneCardCompany = json.onlyOneCardCompany === true;
        let roles = json.roles.map((rjson: any) => { return RolesForCompany.fromJson(rjson)});
        let pendingPhoneNumbers = json.pendingPhoneNumbers.map((pn: any) => PhoneNumber.fromJson(pn));
        let personCompanyRelations = json.companyRelations instanceof Array ? json.companyRelations.map((r: any) => PersonCompanyRelation.fromJson(r)): [];
        let personmergeTime = DateTime.maybeFromJson(json.personmergeTime);
        let emailAddresses: EmailAddress[] = json.emailAddresses instanceof Array ? json.emailAddresses.map((r: any) => EmailAddress.fromJson(r)) : [];
        let pendingEmailAddresses: EmailAddress[] = json.pendingEmailAddresses instanceof Array ? json.pendingEmailAddresses.map((r: any) => EmailAddress.fromJson(r)) : [];

        if(person != null && phoneNumbers != null && cards != null) {
            return new CardUserdata(person, phoneNumbers, cards, tmcids, onlyOneCardCompany, roles, pendingPhoneNumbers,
                personCompanyRelations, personmergeTime, emailAddresses, pendingEmailAddresses);
        } else {
            return null;
        }

    }

    /*
    get cardCompanies() {
        var ret = [];
        this.cards.forEach((c: IdCard) => {
            if(typeof ret[c.orgNum] == "undefined") {
                ret[c.orgNum] = c.company;
            }
        });
        return ret;
    }
    */

}

export class GuiUserlisting {
    constructor(public guid: string, public firstName: string, public lastName: string, public birthDate: DateTime,
                public phones: PhoneNumber[], public verified: boolean, public cardNums: number[], public companyNames: string[]) {}

    get name(): string {
        let fname = this.firstName == null ? "" : this.firstName;
        let lname = this.lastName == null ? "" : this.lastName;
        return (fname+" "+lname).trim();
    }

    static fromJson(json: any): GuiUserlisting {
        let guid = requireStr(json.guid);
        let firstName = maybeStr(json.firstName);
        let lastName = maybeStr(json.lastName);
        let birthDate = noNullApply(json.birthDate, DateTime.fromString)
        let phones: PhoneNumber[] = [];
        if(json.phones instanceof Array) {
            json.phones.forEach((phone: any) => {
                phones.push(PhoneNumber.fromJson(phone));
            });
        }
        let verified = nvl(json.verified, false);
        let cardNums = nvl(json.cardNums, []);
        let companyNames = nvl(json.companyNames, []);
        return new GuiUserlisting(guid, firstName, lastName, birthDate, phones, verified, cardNums, companyNames);
    }
    phoneNumbersJoined(): string {
        return this.phones.map((p) => p.toString()).join(", ");
    }
    cardNumsJoined(): string {
        return this.cardNums.map((n) => n.toString()).join(", ");
    }
}

export class TerminalLocationWithStats  {
    constructor(public termloc: TerminalLocation,public sioCountToday: number) {}

    static fromJson(json: any): TerminalLocationWithStats {
        let termloc = TerminalLocation.fromJson(json.termloc);
        let sioCountToday = requireNum(json.sioCountToday);

        return new TerminalLocationWithStats(termloc, sioCountToday);
    }
}



export class JLocation {
    // time is left as a number (and not DateTime) to be consistent with the protobuf and java model class, making json
    // serialization "just work". But we need to make sure it is an integer, no decimals like iOS seems to put in it.
    constructor(public latitude:number, public longitude:number, public time:number) {
        // Make sure no decimals are included
        this.time = Math.floor(time);
    }

    static fromJson(json: any):JLocation {
        let latitude = requireNum(json.latitude);
        let longitude = requireNum(json.longitude);
        let time = requireNum(json.time);

        return new JLocation(latitude, longitude, time);
    }

    // Returns format google maps expects
    asLatLng(): LatLngLiteral {
        return {lat: this.latitude, lng: this.longitude};
    }

    asGeoPoint(): GeoPoint {
        return new GeoPoint(this.latitude, this.longitude);
    }

    get dateTime(): DateTime {
        return new DateTime(new Date(requireNum(this.time)));
    }

    static fromBrowserPosition(browserPosition: Position): JLocation {
        return new JLocation(browserPosition.coords.latitude, browserPosition.coords.longitude, browserPosition.timestamp);
    }
}

export class GeoPoint {
    constructor(public latitude: number, public longitude: number) {}

    static fromJson(json: any): GeoPoint | null {
        if(json) {
            const lat = requireNum(json.latitude);
            const lon = requireNum(json.longitude);
            return new GeoPoint(lat, lon);
        } else {
            return null;
        }
    }

    asLatLng(): LatLngLiteral {
        return {lat: this.latitude, lng: this.longitude};
    }
}

export class GeoCircle {
    // radius is in meters
    constructor(public center: GeoPoint, public radius: number) {}

    public strokeColor?: string;
    public strokeOpacity?: number;
    public strokeWeight?: number;
    public fillColor?: string;
    public fillOpacity?: number;

    get options(): CircleOptions {
        return {
            clickable: false,
            strokeColor: this.strokeColor,
            strokeOpacity: this.strokeOpacity,
            strokeWeight: this.strokeWeight,
            fillColor: this.fillColor,
            fillOpacity: this.fillOpacity,
        };
    }

    static fromJson(json: any): GeoCircle | null {
        if(json) {
            const center = GeoPoint.fromJson(json.center);
            const radius = requireNum(json.radius);
            if(center != null) {
                return new GeoCircle(center, radius);
            }
        }
        return null;
    }
}

export class GeoPolygon {
    constructor(public points: GeoPoint[]) {}

    public strokeColor?: string;

    get options(): PolygonOptions {
        return {
            clickable: false,
            strokeColor: this.strokeColor
        };
    }

    static fromJson(json: any): GeoPolygon | null {
        if(json && json.points instanceof Array) {
            const points: GeoPoint[] = (json.points as any[]).map((point) => GeoPoint.fromJson(point)).filter((p: GeoPoint | null) => p != null) as GeoPoint[]; // After filter there is no null elements in the array
            return new GeoPolygon(points);
        } else {
            return null;
        }
    }
}

export class UnverifiedPerson {
    private _companyOrgNum: number | null = null;

    constructor(public guid:string, public firstName:string, public lastName:string, public birthDate: DateTime,
                public companyName:string, companyOrgNum: number, public phone:PhoneNumber) {
        if(companyOrgNum > 0) {
            this._companyOrgNum = companyOrgNum;
        }
    }

    get companyOrgNum(): number | null {
        return this._companyOrgNum;
    }

    get name(): string {
        return (nvl(this.firstName, "") + ' ' + nvl(this.lastName, "")).trim();
    }

    static fromJson(json: any):UnverifiedPerson {
        let guid = requireStr(json.guid);
        let firstName = maybeStr(json.firstName);
        let lastName = maybeStr(json.lastName);
        let birthDate = noNullApply(maybeStr(json.birthDate), DateTime.fromString);
        let companyName = maybeStr(json.companyName);
        let companyOrgNum = maybeNum(json.companyOrgNum);
        let phone = PhoneNumber.fromJson(json.phone);

        return new UnverifiedPerson(guid, firstName, lastName, birthDate, companyName, companyOrgNum, phone);
    }
}

export class PersonMergeResult {
    constructor(public movedCardCnt: number, public movedPhoneCnt: number, public movedTmcLinkCnt: number,
                public txGuid: string | null, public txid: number, public fromPersonRef: string, public toPersonRef: string) {
    }

    static fromJson(json: any): PersonMergeResult {
        return new PersonMergeResult(maybeNum(json.movedCardCnt), maybeNum(json.movedPhoneCnt),
            maybeNum(json.movedTmcLinkCnt), maybeStr(json.txGuid), requireNum(json.txid),
            requireStr(json.fromPersonRef), requireStr(json.toPersonRef)
        );
    }
}

export class SiteInOutReportLine {
    timeIn: DateTime;
    timeOut: DateTime | null;
    terminalLocation: TerminalLocation;
    cardNum: number;
    companyName: string;
    companyOrgNum: number;
    firstName: string;
    lastName: string;
    birthDate: DateTime;
    persGuid: string;
    phoneNumber: PhoneNumber;
    expirationDate: DateTime;
    guid: string;
    nameVerified: boolean;
    personPhoneNumbers: PhoneNumber[];


    constructor(timeIn: DateTime, timeOut: DateTime, terminalLocation: TerminalLocation, cardNum: number, companyName: string,
                companyOrgNum: number, firstName: string, lastName: string, birthDate: DateTime, persGuid: string,
                phoneNumber: PhoneNumber, expirationDate: DateTime, guid: string, nameVerified: boolean, personPhoneNumbers: PhoneNumber[]) {
        this.timeIn = timeIn;
        this.timeOut = timeOut;
        this.terminalLocation = terminalLocation;
        this.cardNum = cardNum;
        this.companyName = companyName;
        this.companyOrgNum = companyOrgNum;
        this.firstName = firstName;
        this.lastName = lastName;
        this.birthDate = birthDate;
        this.persGuid = persGuid;
        this.phoneNumber = phoneNumber;
        this.expirationDate = expirationDate;
        this.guid = guid;
        this.nameVerified = nameVerified;
        this.personPhoneNumbers = personPhoneNumbers;
    }

// XXX Consider changing maybeNum to use numOrNull, to avoid having to deal with NaN, just do nullchecking instead.
    static fromJson(json: any): SiteInOutReportLine {
        const timeIn = DateTime.fromString(requireStr(json.timeIn));
        const timeOut = noNullApply(maybeStr(json.timeOut), DateTime.fromString);
        const terminalLocation = TerminalLocation.fromJson(json.terminalLocation);
        const cardNum = maybeNum(json.cardNum);
        const companyName = maybeStr(json.companyName);
        const companyOrgNum = maybeNum(json.companyOrgNum);
        const firstName = maybeStr(json.firstName);
        const lastName = maybeStr(json.lastName);
        const birthDate = noNullApply(maybeStr(json.birthDate), DateTime.fromString);
        const persGuid = requireStr(json.persGuid);
        const phoneNumber = noNullApply(json.phoneNumber, PhoneNumber.fromJson)
        const expirationDate = noNullApply(json.expirationDate, DateTime.fromString);
        const guid = maybeStr(json.guid);
        const nameVerified = json.nameVerified === true;
        const personPhoneNumbers = nonemptyArray(json.personPhoneNumbers) ? json.personPhoneNumbers.map(r => PhoneNumber.fromJson(r)) : [];
        return new SiteInOutReportLine(timeIn, timeOut, terminalLocation, cardNum, companyName, companyOrgNum, firstName, lastName, birthDate, persGuid, phoneNumber, expirationDate, guid, nameVerified, personPhoneNumbers);
    }


    isUvpReg(): Boolean {
        return !this.nameVerified;
    }

    company(): Company {
        return new Company(this.companyName, this.companyOrgNum);
    }
}

export class RegReportResult {
    static readonly rowsPerPage = 400; // Also defined in RegReportResult.scala
    lines: SiteInOutReportLine[];
    hasMore: boolean;

    constructor(lines: SiteInOutReportLine[], hasMore: boolean) {
        this.lines = lines;
        this.hasMore = hasMore;
    }

    static fromJson(json: any) {
        return new RegReportResult(json.lines.map(r => SiteInOutReportLine.fromJson(r)), json.hasMore);
    }
}

export class TerminalLocationHmsregInfo {
    regnum: number | null;
    isCompanyActivated: boolean = false;

    constructor(regnum: number | null, isCompanyActivated: boolean) {
        this.regnum = regnum;
        this.isCompanyActivated = isCompanyActivated;
    }

    /**
     * Sometimes the regnum get set to empty string (from ui input) it seems. This method normalizes it to number or null
     */
    normalizeRegnum() {
        this.regnum = numOrNull(Number(this.regnum));
    }

    static fromJson(json: any): TerminalLocationHmsregInfo {
        let isCompanyActivated = json.isCompanyActivated == true;
        let regnum = numOrNull(json.regnum);
        return new TerminalLocationHmsregInfo(regnum, isCompanyActivated);
    }

    clone(): TerminalLocationHmsregInfo {
        return new TerminalLocationHmsregInfo(this.regnum, this.isCompanyActivated);
    }
}


export class TerminalLocationExportserviceInfo{
    id: number;
    description: string;
    locationExportRef: string;
    active: boolean;

    constructor(id: number, description: string, locationExportRef: string, active: boolean) {
        this.id = id;
        this.description = description;
        this.locationExportRef = locationExportRef;
        this.active = active;
    }

    static fromJson(json: any): TerminalLocationExportserviceInfo {
        let id = requireNum(json.id);
        let description = requireStr(json.description);
        let locationExportRef = maybeStr(json.locationExportRef);
        return new TerminalLocationExportserviceInfo(id, description, locationExportRef, json.active === true);
    }

    clone(): TerminalLocationExportserviceInfo {
        return new TerminalLocationExportserviceInfo(this.id, this.description, this.locationExportRef, this.active);
    }
}

export class TerminalLocationCreate {
    loc: TerminalLocation;
    hmsregInfo: TerminalLocationHmsregInfo | null;
    exportserviceInfos: TerminalLocationExportserviceInfo[];


    constructor(loc: TerminalLocation, hmsregInfo: TerminalLocationHmsregInfo | null, exportserviceInfos: TerminalLocationExportserviceInfo[]) {
        this.loc = loc;
        this.hmsregInfo = hmsregInfo;
        this.exportserviceInfos = exportserviceInfos;
    }

    static fromJson(json: any): TerminalLocationCreate {
        let loc = TerminalLocation.fromJson(json.loc);
        let hmsregInfo = json.hmsregInfo != null ? TerminalLocationHmsregInfo.fromJson(json.hmsregInfo) : null;
        let exportserviceInfos = json.exportserviceInfos.map((expi: any) => TerminalLocationExportserviceInfo.fromJson(expi));
        return new TerminalLocationCreate(loc, hmsregInfo, exportserviceInfos);
    }

    toJson(): string {
        let jsLoc = this.loc.toJson();
        let jsHmsregInfo = JSON.stringify(this.hmsregInfo);
        let jsExportserviceInfos = JSON.stringify(this.exportserviceInfos);
        return '{"loc": '+jsLoc+', "hmsregInfo": '+jsHmsregInfo+', "exportserviceInfos": '+jsExportserviceInfos+'}';
    }
}

export class TerminalConfigNext {
    constructor(public guid: string, public description: string, public location: TerminalLocation | null,
                public companyOrgnum: number, public updated: DateTime, public confirmed: DateTime | null,
                public deviceid: string | null, public existingTerminal: boolean | null, public continuousPushlistener: boolean) {}

    get pending() {
        return this.confirmed == null || this.confirmed.isBefore(this.updated);
    }

    static fromJson(json: any) {
        let guid = requireStr(json.guid);
        let description = requireStr(json.description);
        let location = json.location != null ? TerminalLocation.fromJson(json.location) : null;
        let companyOrgnum = requireNum(json.companyOrgnum);
        let updated = DateTime.fromJson(json.updated);
        let confirmed = DateTime.maybeFromJson(json.confirmed);
        let deviceid = maybeStr(json.deviceid);
        let existingTerminal = json.existingTerminal !== null ? json.existingTerminal : null;
        let contPush = json.continuousPushlistener === true;

        return new TerminalConfigNext(guid, description, location, companyOrgnum, updated, confirmed, deviceid, existingTerminal, contPush);
    }
}

export class InvoicingExceptedTerminal {
    constructor(public id: number, public terminalRef: string, public fromTs: DateTime, public toTs: DateTime, public reason: string) {}

    static fromJson(json: any) {
        let id = requireNum(json.id);
        let terminalRef = requireStr(json.terminalRef);
        let fromTs = DateTime.fromJson(json.fromTs);
        let toTs = DateTime.fromJson(json.toTs);
        let reason = requireStr(json.reason);
        return new InvoicingExceptedTerminal(id, terminalRef, fromTs, toTs, reason);
    }
}

export class InvoicingExceptedLocation {
    constructor(public id: number, public locationRef: number, public fromTs: DateTime, public toTs: DateTime, public reason: string) {}

    static fromJson(json: any) {
        let id = requireNum(json.id);
        let locationRef = requireNum(json.locationRef);
        let fromTs = DateTime.fromJson(json.fromTs);
        let toTs = DateTime.fromJson(json.toTs);
        let reason = requireStr(json.reason);
        return new InvoicingExceptedLocation(id, locationRef, fromTs, toTs, reason);
    }
}

export class ExportserviceHeaderRow {
    constructor (public id: number, public description: string, public companyOrgnum: number, public urlSio: string,
                 public username: string | null, public secret: string | null, public error: string | null, public authMethod: string = ExportserviceHeaderRow.authMethodBasic) {}

    // Also defined in constants.go
    static readonly authMethodBasic = "basic";
    static readonly authMethodUrl = "url";

    copy() {
        return new ExportserviceHeaderRow(this.id, this.description, this.companyOrgnum, this.urlSio, this.username, this.secret, this.error, this.authMethod);
    }

    static fromJson(json: any) {
        const id = requireNum(json.id);
        const description = requireStr(json.description);
        const companyOrgnum = requireNum(json.companyOrgnum);
        const urlSio = requireStr(json.urlSio);
        const username = maybeStr(json.username);
        const secret = maybeStr(json.secret);
        const error = maybeStr(json.error);
        const authMethod = maybeStr(json.authMethod) || ExportserviceHeaderRow.authMethodBasic;
        return new ExportserviceHeaderRow(id, description, companyOrgnum, urlSio, username, secret, error, authMethod);
    }
}

export class HmsregIntegrationCompany {
    constructor(public companyOrgnum: number, public hmsregUsername: string | null, public hmsregPassword: string | null,
                public hmsreg365Username: string | null, public hmsreg365Password: string | null) {}

    static fromJson(json: any) {
        const companyOrgnum = requireNum(json.companyOrgnum);
        const hmsregUsername = maybeStr(json.hmsregUsername);
        const hmsregPassword = maybeStr(json.hmsregPassword);
        const hmsreg365Username = maybeStr(json.hmsreg365Username);
        const hmsreg365Password = maybeStr(json.hmsreg365Password);
        return new HmsregIntegrationCompany(companyOrgnum, hmsregUsername, hmsregPassword, hmsreg365Username, hmsreg365Password);
    }
}

export class ProresultIntegrationCompanySettings {
    constructor(public orgnum: number, public proresultCompanyId: string, public statusText: string | null, public enabled: boolean) {}

    static fromJson(json: any) {
        const orgnum = requireNum(json.orgnum);
        const proresultCompanyId = requireStr(json.proresultCompanyId);
        const statusText = maybeStr(json.statusText);
        const enabled = json.enabled === true;
        return new ProresultIntegrationCompanySettings(orgnum, proresultCompanyId, statusText, enabled);
    }
}

export class PhonelistReportRow{
    personGuid: string;
    firstName: string | null;
    lastName: string | null;
    birthDate: DateTime | null;
    phone: PhoneNumber;
    companies: UnverifiedCompany[];
    lastInTime: DateTime;


    constructor(personGuid: string, firstName: string | null, lastName: string | null, birthDate: DateTime | null, phone: PhoneNumber, companies: UnverifiedCompany[], lastInTime: DateTime) {
        this.personGuid = personGuid;
        this.firstName = firstName;
        this.lastName = lastName;
        this.birthDate = birthDate;
        this.phone = phone;
        this.companies = companies;
        this.lastInTime = lastInTime;
    }

    static fromJson(json: any) {
        const personGuid = requireStr(json.personGuid);
        const firstName = maybeStr(json.firstName);
        const lastName = maybeStr(json.lastName);
        const birthDate = noNullApply(maybeStr(json.birthDate), DateTime.fromString);
        const phone = PhoneNumber.fromJson(json.phone);
        const companies = json.companies.map((c: any) => UnverifiedCompany.fromJson(c));
        const lastInTime = DateTime.fromJson(json.lastInTime);

        return new PhonelistReportRow(personGuid, firstName, lastName, birthDate, phone, companies, lastInTime);
    }
}

export class AllUsersReportRow{
    personGuid: string;
    firstName: string | null;
    lastName: string | null;
    birthDate: DateTime | null;
    phones: PhoneNumber[];
    companies: UnverifiedCompany[];
    cardNums: number[];


    constructor(personGuid: string, firstName: string | null, lastName: string | null, birthDate: DateTime | null, phones: PhoneNumber[], companies: UnverifiedCompany[], cardNums: number[]) {
        this.personGuid = personGuid;
        this.firstName = firstName;
        this.lastName = lastName;
        this.birthDate = birthDate;
        this.phones = phones;
        this.companies = companies;
        this.cardNums = cardNums;
    }

    static fromJson(json: any) {
        const personGuid = requireStr(json.personGuid);
        const firstName = maybeStr(json.firstName);
        const lastName = maybeStr(json.lastName);
        const birthDate = noNullApply(maybeStr(json.birthDate), DateTime.fromString);
        const phones = json.phones.map((p: any) => PhoneNumber.fromJson(p));
        const companies = json.companies.map((c: any) => UnverifiedCompany.fromJson(c));
        const cardNums = json.cardNums.map((n: any) => requireNum(n));

        return new AllUsersReportRow(personGuid, firstName, lastName, birthDate, phones, companies, cardNums);
    }
}


export class UserSiteReportRow {
    constructor(public inTime: DateTime, public outTime: DateTime | null) {}

    static fromJson(json: any) {
        const inTime = DateTime.fromJson(json.in);
        const outTime = DateTime.maybeFromJson(json.out);
        return new UserSiteReportRow(inTime, outTime);
    }
}

export class CountryCode {
    code: number;
    name: string;


    constructor(code: number, name: string) {
        this.code = code;
        this.name = name;
    }

    get prefixed(): string {
        return `+${this.code}`;
    }
    get label(): string {
        return `+${this.code} (${this.name})`;
    }

    static fromJson(json: any) {
        const code = requireNum(json.code);
        const name = requireStr(json.name);
        return new CountryCode(code, name);
    }
}

export class ApiMessage {
    private pNum: number;
    private pMsg: string;

    constructor(num: number, msg: string) {
        this.pNum = num;
        this.pMsg = msg;
    }
    get num() {
        return this.pNum;
    }
    get msg() {
        return this.pMsg;
    }

    static fromJson(json: any) {
        const num = requireNum(json.num);
        const msg = requireStr(json.msg);
        return new ApiMessage(num, msg);
    }
}

export class CompanyRole {
    constructor(public company: Company, public role: Role) {}

    static fromJson(json: any) {
        const company = Company.fromJson(json.company);
        const role = Role.fromJson(json.role);
        return new CompanyRole(company, role);
    }
}

export class LocationRole {
    constructor(public location: TerminalLocation, public role: Role) {}

    static fromJson(json: any) {
        const location = TerminalLocation.fromJson(json.location);
        const role = Role.fromJson(json.role);
        return new LocationRole(location, role);
    }
}

export class PendingPerson {
    constructor(
        public guid: string | null,
        public firstName: string,
        public lastName: string,
        public birthDate: string | null,
        public phone: PhoneNumber,
        public employedAt: Company[],
        public insertTs: DateTime,
        public insertByName: string,
        public companyRoles: CompanyRole[],
        public locationRoles: LocationRole[],
    ) {}

    birthDateToDateTime(): DateTime | null {
        return this.birthDate != null ? DateTime.fromDayParam(this.birthDate) : null;
    }

    static fromJson(json: any) {
        let guid = requireStr(json.guid);
        let firstName = requireStr(json.firstName);
        let lastName = requireStr(json.lastName);
        let birthDate = maybeStr(json.birthDate);
        let phone = PhoneNumber.fromJson(json.phone);
        let employedAt = json.employedAt instanceof Array ? json.employedAt.map((r: any) => Company.fromJson(r)) : [];
        let insertTs = DateTime.fromJson(json.insertTs);
        let insertByName = maybeStr(json.insertByName);
        let companyRoles = json.companyRoles instanceof Array ? json.companyRoles.map(CompanyRole.fromJson) : [];
        let locationRoles = json.locationRoles instanceof Array ? json.locationRoles.map(LocationRole.fromJson) : [];
        return new PendingPerson(guid, firstName, lastName, birthDate, phone, employedAt, insertTs, insertByName, companyRoles, locationRoles);
    }
}

export class PendingPhoneconfirm {
    constructor(
        public existingPerson: boolean,
        public personGuid: string,
        public firstName: string,
        public lastName: string,
        public birthDate: string | null,
        public phone: PhoneNumber,
        public employedAt: Company[],
        public insertTs: DateTime,
    ) {}

    birthDateToDateTime(): DateTime | null {
        return this.birthDate != null ? DateTime.fromDayParam(this.birthDate) : null;
    }

    static fromJson(json: any) {
        let existingPerson = json.existingPerson !== false;
        let personGuid = requireStr(json.personGuid);
        let firstName = requireStr(json.firstName);
        let lastName = requireStr(json.lastName);
        let birthDate = maybeStr(json.birthDate);
        let phone = PhoneNumber.fromJson(json.phone);
        let employedAt = json.employedAt instanceof Array ? json.employedAt.map((r: any) => Company.fromJson(r)) : [];
        let insertTs = DateTime.fromJson(json.insertTs);
        return new PendingPhoneconfirm(existingPerson, personGuid, firstName, lastName, birthDate, phone, employedAt, insertTs);
    }
}


export class PersonCompanyRelation {
    constructor (public id: number, public personRef: string, public company: Company, public fromTs: DateTime,
                 public toTs: DateTime | null, public relationType: string) {}

    static fromJson(json: any) {
        let id = requireNum(json.id);
        let personRef = requireStr(json.personRef);
        let company = Company.fromJson(json.company);
        let fromTs = DateTime.fromJson(json.fromTs);
        let toTs = DateTime.maybeFromJson(json.toTs);
        let relationType = requireStr(json.relationType);
        return new PersonCompanyRelation(id, personRef, company, fromTs, toTs, relationType);
    }
}

export class PendingCardConfirm {
    public pendingPerson: Person | null = null;
    public existingPerson: Person | null;
    public mergeAllowed: boolean;
    public phone: PhoneNumber;

    constructor(pendingPerson: Person | null, existingPerson: Person | null, mergeAllowed: boolean, phone: PhoneNumber) {
        this.pendingPerson = pendingPerson;
        this.existingPerson = existingPerson;
        this.mergeAllowed = mergeAllowed;
        this.phone = phone;
    }

    static fromJson(json: any) {
        const pendingPerson = Person.fromJson(json.pendingPerson);
        const existingPerson = noNullApply(json.existingPerson, Person.fromJson);
        const mergeAllowed = json.mergeAllowed === true;
        const phone = PhoneNumber.fromJson(json.phone);
        return new PendingCardConfirm(pendingPerson, existingPerson, mergeAllowed, phone);
    }
}

export class ActiveSio {
    public terminalLocation: TerminalLocation;
    public timeIn: DateTime;
    public guid: string;
    public regreminder: boolean;
    public regreminderAvailable: boolean;

    constructor(terminalLocation: TerminalLocation, timeIn: DateTime, guid: string, regreminder: boolean, regreminderAvailable: boolean) {
        this.terminalLocation = terminalLocation;
        this.timeIn = timeIn;
        this.guid = guid;
        this.regreminder = regreminder;
        this.regreminderAvailable = regreminderAvailable;
    }

    static fromJson(json: any) {
        const terminalLocation = TerminalLocation.fromJson(json.terminalLocation);
        const timeIn = DateTime.fromJson(json.timeIn);
        const guid = requireStr(json.guid);
        return new ActiveSio(terminalLocation, timeIn, guid, json.regreminder, json.regreminderAvailable);
    }
}

export class TerminalLocationShortInfo {
    constructor(public id: number, public description: string, public company: Company) {}

    static fromJson(json: any) {
        return new TerminalLocationShortInfo(requireNum(json.id), requireStr(json.description), Company.fromJson(json.company))
    }
}

export class LastIn {
    constructor(public terminalLocation: TerminalLocationShortInfo, public timeIn: DateTime) {}

    static fromJson(json: any) {
        return new LastIn(TerminalLocationShortInfo.fromJson(json.terminalLocation), DateTime.fromJson(json.timeIn));
    }
}

export class TerminalLocationArea {
    constructor(public id: number, public terminalLocationId: number, public geo: GeoCircle | GeoPolygon | null) {}

    static fromJson(json: any) {
        const id = requireNum(json.id);
        const tlId = requireNum(json.terminalLocationId);
        let geo: GeoCircle | GeoPolygon | null = null;
        if(json.geo) {
            if(json.geo.radius) {
                geo = GeoCircle.fromJson(json.geo);
            } else if(json.geo.points) {
                geo = GeoPolygon.fromJson(json.geo);
            }
        }
        return new TerminalLocationArea(id, tlId, geo);
    }
}

export class TerminalLocationGeofence {
    constructor(public id: number, public terminalLocationId: number, public geo: GeoCircle) {
        // Geofence circles should have a low-key grey stroke color by default
        this.geo.strokeColor = UnimportantColor;
        this.geo.strokeWeight = 1.0;
        this.geo.strokeOpacity = 0.6;
        this.geo.fillColor = UnimportantColor;
        this.geo.fillOpacity = 0.4;
    }

     static fromJson(json: any) {
        const id = requireNum(json.id);
        const tlId = requireNum(json.terminalLocationId);
        const geo: GeoCircle | null = GeoCircle.fromJson(json.geo);
        if(geo == null) {
            throw new Error(`geo cannot be null in geofence`);
        }

        return new TerminalLocationGeofence(id, tlId, geo);
    }
}

export class InOutAvailableLocation {
    constructor(public locid: number,
                public locDescription: string,
                public locAddress: string,
                public company: Company,
                public unverifiedRegOk: boolean,
                public areas: TerminalLocationArea[],
                public terminalPositions: GeoPoint[],
                public geoRequired: boolean,
                public regcodeRequired: boolean,
                public regcodeRegistered: boolean,
                public regcodeExpired: DateTime | null,
                public hseGuidelineEnabled: number,
                public hseGuideline: HseGuideline | null,
                public hseGuidelineConfirmed: boolean,
    ) {}

    static fromJson(json: any) {
        const locid = requireNum(json.locid);
        const locDescr = requireStr(json.locDescription);
        const locAddr = maybeStr(json.locAddress);
        const company = Company.fromJson(json.company);
        const unvRegOk = json.unverifiedRegOk === true;
        const areas = json.areas instanceof Array ? json.areas.map(TerminalLocationArea.fromJson) : [];
        const tPositions = json.terminalPositions instanceof Array ? json.terminalPositions.map(GeoPoint.fromJson) : [];
        const geoRequired = json.geoRequired !== false;
        const regcodeRequired = json.regcodeRequired === true;
        const regcodeRegistered = json.regcodeRegistered === true;
        const regcodeExpired = DateTime.maybeFromJson(json.regcodeExpired);
        const hseGuidelineEnabled = requireNum(json.hseGuidelineEnabled);
        const hseGuideline = json.hseGuideline ? HseGuideline.fromJson(json.hseGuideline) : null;
        const hseGuidelineConfirmed = json.hseGuidelineConfirmed === true;

        return new InOutAvailableLocation(
            locid, locDescr, locAddr, company, unvRegOk, areas, tPositions, geoRequired,
            regcodeRequired, regcodeRegistered, regcodeExpired, hseGuidelineEnabled, hseGuideline, hseGuidelineConfirmed,
        );
    }

    get regcodeGood() {
      return !this.regcodeRequired || (this.regcodeRegistered && this.regcodeExpired === null);
    }
}

export class PersonConfig {
    /** loaded: true when config is loaded from server this session */
    constructor(public loaded: boolean, public cardAddDismissed: boolean, public qrCardConnectAvailable: boolean, public regreminderEnabled: boolean) {}

    static default() {
        return new PersonConfig(false, true, false, false);
    }

    static fromJson(json: any) {
        const loaded = json.loaded === true;
        const dismissed = json.cardAddDismissed === true;
        const qrConnectAvailable = json.qrCardConnectAvailable === true;
        return new PersonConfig(loaded, dismissed, qrConnectAvailable, json.regreminderEnabled);
    }
}


export class SimRow {

    constructor(public simSerial: string, public phoneNumber: number, public operator: string, public dataMegaBytesThisMonth: number | null,
                public subscriptionStatusReported: string | null, public subscriptionStatusWanted: string | null,
                public subscriptionStatusReportedTs: DateTime, public subscriptionStatusWantedTs: DateTime | null) {}

    static fromJson(json: any) {
        const simSerial = requireStr(json.simSerial);
        const phoneNumber = requireNum(json.phoneNumber);
        const operator = requireStr(json.operator);
        const dataMbTM = maybeNum(json.dataMegabytesThisMonth);
        const subscrStatusRep = maybeStr(json.subscriptionStatusReported);
        const subscrStatusWant = maybeStr(json.subscriptionStatusWanted);
        const subscrStatusRepTs = DateTime.fromJson(json.subscriptionStatusReportedTs);
        const subscrStatusWantTs = noNullApply(json.subscriptionStatusWantedTs, DateTime.fromJson);
        return new SimRow(simSerial, phoneNumber, operator, dataMbTM, subscrStatusRep, subscrStatusWant, subscrStatusRepTs, subscrStatusWantTs);
    }
}

export interface TerminalWithoutSIM {
    terminalGuid: string;
    terminalDescription: string;
    companyOrgnum: number;
    companyName: string;
    phoneDeviceid: string | null;
    lastSyncAt: string | null;
}

export class SimAndTerminalReportInfo {
    constructor(public terminal: Terminal | null, public simRow: SimRow) {}

    static fromJson(json: any): SimAndTerminalReportInfo {
        return new SimAndTerminalReportInfo(json.terminal ? Terminal.fromJson(json.terminal) : null, SimRow.fromJson(json.simRow));
    }
}

export class Com4ManualOperation {
    constructor(public id: string, public target: string, public state: string,
                public createdAt: DateTime, public completedAt: DateTime | null,
                public requestedBy: string | null, public error: string | null, public requestedByName: string | null = null) {}

    static fromJson(json: any): Com4ManualOperation {
        return new Com4ManualOperation(requireStr(json.id), requireStr(json.target), requireStr(json.state),
            DateTime.fromJson(json.createdAt), DateTime.maybeFromJson(json.completedAt), maybeStr(json.requestedBy), maybeStr(json.error), maybeStr(json.requestedByName));
    }
}

export class Com4ReconciliationReportRow {
    constructor(
        public simSerial: string,
        public atPresent: boolean,
        public phoneNumber: number | null,
        public dataMegabytesThisMonth: number | null,
        public reportedStatus: string | null,
        public reportedStatusAt: DateTime | null,
        public wantedStatus: string | null,
        public wantedStatusAt: DateTime | null,
        public inventorySeenAt: DateTime | null,
        public providerObservedAt: DateTime | null,
        public providerFound: boolean | null,
        public providerKnown: boolean | null,
        public providerRawStatus: string | null,
        public providerNormalizedStatus: string | null,
        public providerError: string | null,
        public providerHttpStatus: number | null,
        public observationSource: string | null,
        public observationLifecycleOperationId: string | null,
        public classification: string,
        public reason: string,
        public operationId: string | null,
        public operationTargetStatus: string | null,
        public operationState: string | null,
        public operationSource: string | null,
        public operationCreatedAt: DateTime | null,
        public operationExpiresAt: DateTime | null,
        public operationSentAt: DateTime | null,
        public operationAcceptedAt: DateTime | null,
        public operationCompletedAt: DateTime | null,
        public operationLastError: string | null,
        public operationRequestedByPersonRef: string | null,
        public operationEventCount: number,
        public intentId: string | null,
        public intentTargetStatus: string | null,
        public intentState: string | null,
        public intentSource: string | null,
        public intentCreatedAt: DateTime | null,
        public intentExpiresAt: DateTime | null,
        public intentCompletedAt: DateTime | null,
        public intentOperationId: string | null,
        public intentCompletionReason: string | null,
        public terminalGuid: string | null,
        public terminalDescription: string | null,
        public companyOrgnum: number | null,
        public terminalDeactivatedAt: DateTime | null,
        public lastManualOperation: Com4ManualOperation | null = null,
        public usageAllowance: Com4UsageAllowance | null = null,
        public usageObservedAt: DateTime | null = null,
        public usageError: string | null = null,
    ) {}

    static fromJson(json: any): Com4ReconciliationReportRow {
        return new Com4ReconciliationReportRow(
            requireStr(json.simSerial),
            json.atPresent === true,
            numOrNull(json.phoneNumber),
            numOrNull(json.dataMegabytesThisMonth),
            maybeStr(json.reportedStatus),
            DateTime.maybeFromJson(json.reportedStatusAt),
            maybeStr(json.wantedStatus),
            DateTime.maybeFromJson(json.wantedStatusAt),
            DateTime.maybeFromJson(json.inventorySeenAt),
            DateTime.maybeFromJson(json.providerObservedAt),
            typeof json.providerFound === "boolean" ? json.providerFound : null,
            typeof json.providerKnown === "boolean" ? json.providerKnown : null,
            maybeStr(json.providerRawStatus),
            maybeStr(json.providerNormalizedStatus),
            maybeStr(json.providerError),
            numOrNull(json.providerHttpStatus),
            maybeStr(json.observationSource),
            maybeStr(json.observationLifecycleOperationId),
            requireStr(json.classification),
            requireStr(json.reason),
            maybeStr(json.operationId),
            maybeStr(json.operationTargetStatus),
            maybeStr(json.operationState),
            maybeStr(json.operationSource),
            DateTime.maybeFromJson(json.operationCreatedAt),
            DateTime.maybeFromJson(json.operationExpiresAt),
            DateTime.maybeFromJson(json.operationSentAt),
            DateTime.maybeFromJson(json.operationAcceptedAt),
            DateTime.maybeFromJson(json.operationCompletedAt),
            maybeStr(json.operationLastError),
            maybeStr(json.operationRequestedByPersonRef),
            requireNum(json.operationEventCount),
            maybeStr(json.intentId),
            maybeStr(json.intentTargetStatus),
            maybeStr(json.intentState),
            maybeStr(json.intentSource),
            DateTime.maybeFromJson(json.intentCreatedAt),
            DateTime.maybeFromJson(json.intentExpiresAt),
            DateTime.maybeFromJson(json.intentCompletedAt),
            maybeStr(json.intentOperationId),
            maybeStr(json.intentCompletionReason),
            maybeStr(json.terminalGuid),
            maybeStr(json.terminalDescription),
            numOrNull(json.companyOrgnum),
            DateTime.maybeFromJson(json.terminalDeactivatedAt),
            json.lastManualOperation == null ? null : Com4ManualOperation.fromJson(json.lastManualOperation),
            json.usageAllowance == null ? null : Com4UsageAllowance.fromJson(json.usageAllowance),
            DateTime.maybeFromJson(json.usageObservedAt),
            maybeStr(json.usageError),
        );
    }
}

export class Com4InventoryRunReport {
    constructor(
        public runId: number,
        public startedAt: DateTime | null,
        public completedAt: DateTime | null,
        public success: boolean | null,
        public listedSimCount: number | null,
        public error: string | null,
        public requestedAt: DateTime | null = null,
        public requestedByPersonRef: string | null = null,
        public usageFailureCount: number = 0,
        public observedSimCount: number = 0,
    ) {}

    static fromJson(json: any): Com4InventoryRunReport {
        return new Com4InventoryRunReport(
            requireNum(json.runId),
            DateTime.maybeFromJson(json.startedAt),
            DateTime.maybeFromJson(json.completedAt),
            typeof json.success === "boolean" ? json.success : null,
            numOrNull(json.listedSimCount),
            maybeStr(json.error),
            DateTime.maybeFromJson(json.requestedAt),
            maybeStr(json.requestedByPersonRef),
            maybeNum(json.usageFailureCount) || 0,
            maybeNum(json.observedSimCount) || 0,
        );
    }
}

export interface Com4ManualHistoryEntry {
    controlId: number;
    changedAt: string;
    changedByPersonRef: string;
    changedByName: string | null;
    reason: string;
    simCount: number;
    added: string[];
    removed: string[];
}

export interface Com4OperationEvent {
    id: number;
    occurredAt: string;
    previousState: string | null;
    nextState: string;
    message: string | null;
}

export class Com4ManualSIMs {
    constructor(
        public controlId: number,
        public simSerials: string[],
        public changedAt: DateTime,
        public changedByPersonRef: string,
        public reason: string,
        public changedByName: string | null = null,
    ) {}

    static fromJson(json: any): Com4ManualSIMs {
        if (!(json.simSerials instanceof Array)) throw new JsonParseException("Manuelle COM4-SIM må være en liste");
        return new Com4ManualSIMs(
            requireNum(json.controlId),
            json.simSerials.map((simSerial: any) => requireStr(simSerial)),
            DateTime.fromJson(json.changedAt),
            requireStr(json.changedByPersonRef),
            requireStr(json.reason),
            maybeStr(json.changedByName),
        );
    }
}

export class Com4UsageAllowance {
    constructor(public id: number, public limitMB: number | null, public month: string,
                public changedAt: DateTime, public changedBy: string, public reason: string, public changedByName: string | null = null) {}

    static fromJson(json: any): Com4UsageAllowance {
        return new Com4UsageAllowance(requireNum(json.id), json.limitMB == null ? null : requireNum(json.limitMB), requireStr(json.month),
            DateTime.fromJson(json.changedAt), requireStr(json.changedBy), requireStr(json.reason), maybeStr(json.changedByName));
    }
}

export class Com4ReconciliationReport {
    constructor(
        public generatedAt: DateTime,
        public observationStaleAfterHours: number,
        public latestInventoryRun: Com4InventoryRunReport | null,
        public latestSuccessfulInventoryAt: DateTime | null,
        public manualControl: Com4ManualSIMs | null,
        public rows: Com4ReconciliationReportRow[],
        public usageMonth: string | null = null,
    ) {}

    static fromJson(json: any): Com4ReconciliationReport {
        if (!(json.rows instanceof Array)) throw new JsonParseException("COM4 reconciliation rows must be an array");
        // Old servers omit this field. That is not evidence of an empty list.
        if (json.manualControl === undefined) {
            throw new JsonParseException("Rapporten mangler informasjon om manuelle SIM. Last siden på nytt.");
        }
        return new Com4ReconciliationReport(
            DateTime.fromJson(json.generatedAt),
            requireNum(json.observationStaleAfterHours),
            json.latestInventoryRun == null ? null : Com4InventoryRunReport.fromJson(json.latestInventoryRun),
            DateTime.maybeFromJson(json.latestSuccessfulInventoryAt),
            json.manualControl === null ? null : Com4ManualSIMs.fromJson(json.manualControl),
            json.rows.map((row: any) => Com4ReconciliationReportRow.fromJson(row)),
            maybeStr(json.usageMonth),
        );
    }
}

export class PendingCardReply {
    constructor(public nextOp: number, public card: IdCard, public otherPerson: Person | null, public otherPersonPhones: PhoneNumber[]) {}

    static ALREADY_CONNECTED = 1;
    static MERGE = 2; // Implies that takeover is also allowed
    static TAKEOVER = 3;
    static ADD_NEW = 4;

    static fromJson(json: any) {
        const nextOp = requireNum(json.nextOp);
        let card = IdCard.fromJson(json.card);
        let otherPerson: Person | null = null;
        if(json.otherPerson) {
            otherPerson = Person.fromJson(json.otherPerson);
        }
        let phones: PhoneNumber[] = [];
        if(json.otherPersonPhones instanceof Array) {
            phones = (json.otherPersonPhones as any[]).map(PhoneNumber.fromJson);
        }
        return new PendingCardReply(nextOp, card, otherPerson, phones);
    }
}

export class PersonmergeUndoInfo {
    constructor(public prevPerson: Person, public mergeTime: DateTime, public txid: number, public mergedCardNums: number[]) {}

    static fromJson(json: any) {
        const mergedCardNums = json.mergedCardNums instanceof Array ? json.mergedCardNums.map(requireNum) : [];
        const prevPerson = Person.fromJson(json.prevPerson);
        if(prevPerson != null) {
            return new PersonmergeUndoInfo(prevPerson, DateTime.fromJson(json.mergeTime), requireNum(json.txid), mergedCardNums);
        } else {
            throw new IllegalArgumentException("PersonmergeUndoInfo json must contain prevPerson");
        }
    }
}

export class PhoneCheckReply {
    constructor(public replyNum: number, public created: DateTime | null = null) {}

    static fromJson(json: any) {
        const n = requireNum(json.replyNum);
        const created = DateTime.maybeFromJson(json.created);
        return new PhoneCheckReply(n, created);
    }
}

export class PhoneCheckReplies {
    constructor(public replies: PhoneCheckReply[]) {}

    static PENDING_CODE = 1;
    static CONFIRMED_CODE = 2;

    static fromJson(json: any) {
        let replies: PhoneCheckReply[] = [];
        if(json.replies instanceof Array) {
            replies = (json.replies as any[]).map(PhoneCheckReply.fromJson);
        }
        return new PhoneCheckReplies(replies);
    }
}

export class BillingInfoLocation {
    constructor(public  locationId: number, public locationDescription: string, public invoiceRef: string | null, public exceptedBecauseTerminal: boolean, public exceptionReason: string | null) {}

    static fromJson(json: any): BillingInfoLocation {
        return new BillingInfoLocation(
            requireNum(json.locationId),
            requireStr(json.locationDescription),
             maybeStr(json.invoiceRef),
            json.exceptedBecauseTerminal === true,
            maybeStr(json.exceptionReason),
        );
    }
}

export class BillingInfoTerminal {
    constructor(public terminalGuid: string, public terminalDescription: string, public exceptionReason: string | null,
                public invoiceRef: string | null, public locationId: number | null, public locationDescription: string | null,
                public licenseType: number) {}

    static fromJson(json: any) {
        return new BillingInfoTerminal(
            requireStr(json.terminalGuid),
            requireStr(json.terminalDescription),
            maybeStr(json.exceptionReason),
            maybeStr(json.invoiceRef),
            maybeNum(json.locationId),
            maybeStr(json.locationDescription),
            requireNum(json.licenseType),
        );
    }
}

export class SimReportRow {
    public simSerial: string;
    public phoneNumber: number;
    public accountNumber: number;
    public firstUsed: DateTime | null = null;
    public lastUsed: DateTime | null = null;
    public statusTxt: string | null = null;


    constructor(simSerial: string, phoneNumber: number, accountNumber: number) {
        this.simSerial = simSerial;
        this.phoneNumber = phoneNumber;
        this.accountNumber = accountNumber;
    }
}

export class TerminalLocationRegcode {
    constructor(public id: number, public terminalLocationId: number, public toTs: DateTime | null, public code: string) {}

    get regcodeFormatted(): string {
        if (this.code.length === 5) {
            return `${this.code.slice(0, 3)} ${this.code.slice(3, 5)}`;
        } else {
            return this.code;
        }
    }

    static fromJson(json: any) {
        return new TerminalLocationRegcode(
            requireNum(json.id),
            requireNum(json.terminalLocationId),
            DateTime.maybeFromJson(json.toTs),
            requireStr(json.code),
        );
    }
}

export class RegcodeCheckReply {
    constructor(public expired: DateTime | null, public location: TerminalLocation | null, public valid: boolean) {}

    static fromJson(json: any) {
        const location = json.location ? TerminalLocation.fromJson(json.location) : null;
        return new RegcodeCheckReply(
            DateTime.maybeFromJson(json.expired),
            location,
            json.valid === true,
        );
    }
}

export class SnackMsg {
    private pColor?: string;
    private pMsg: string;

    constructor(msg: string, color?: string) {
        this.pMsg = msg;
        this.pColor = color;
    }

    get color() {
        return this.pColor;
    }
    get msg() {
        return this.pMsg;
    }
}

export class CompanyAccount {
    constructor(public company: Company,
                public initBy: string,
                public initTs: DateTime,
                public initStep: number,
                public invoiceEmail: string | null,
                public comment: string | null,
                public orderRightConfirmed: boolean,
                public systemTermsConfirmed: boolean,
                public freeUntilTs: DateTime,
    ) {}

    static freeUntilTsCalc(): DateTime {
        return DateTime.now().dayOffset(14);
    }

    // First init of CompanyAccount object only specifies company and user.
    static init(company: Company, initBy: string) {
        return new CompanyAccount(company, initBy, DateTime.now(), 1, null, null, false, false, this.freeUntilTsCalc());
    }

    static fromJson(json: any) {
        return new CompanyAccount(
            Company.fromJson(json.company),
            maybeStr(json.initBy),
            DateTime.fromJson(json.initTs),
            requireNum(json.initStep),
            maybeStr(json.invoiceEmail),
            maybeStr(json.comment),
            json.orderRightConfirmed === true,
            json.systemTermsConfirmed === true,
            DateTime.fromJson(json.freeUntilTs || json.initTs),
        );
    }
}

export class AccountQuotas {
    constructor(public locationInitWeek: number, public sioLocationDay: number) {}

    static fromJson(json: any) {
        return new AccountQuotas(
            requireNum(json.locationInitWeek),
            requireNum(json.sioLocationDay),
        );
    }
}

export class DeliveryAddress {
    constructor(
        public name: string | null,
        public address1: string | null,
        public address2: string | null,
        public postnumber: string | null,
        public postplace: string | null,
    ) {}

    static newEmpty() {
        return new DeliveryAddress(null, null, null, null, null);
    }

    static fromJson(json: any) {
        return new DeliveryAddress(
            maybeStr(json.name),
            maybeStr(json.address1),
            maybeStr(json.address2),
            maybeStr(json.postnumber),
            maybeStr(json.postplace),
        );
    }
}

export enum ItemUnit {
    pcs = "stk",
}

export interface PurchaseItem {
    productId: number; // Id of the ordered product
    name: string;
    subtext: string; // Short extra text printed below name sometimes.
    amount: number; // Amount of the product to be purchased
    unit: ItemUnit;
    price: number;

    clone(): PurchaseItem;
}

const purchaseItemFromJson = (json: any): PurchaseItem => {
    switch (json.productId) {
        case TerminalPurchaseItem.pId:
            return TerminalPurchaseItem.fromJson(json);

        case TerminalSignPurchaseItem.pId:
            return TerminalSignPurchaseItem.fromJson(json);

        case TerminalBatteryPurchaseItem.pId:
            return TerminalBatteryPurchaseItem.fromJson(json);

        case ChargerPurchaseItem.pId:
            return ChargerPurchaseItem.fromJson(json);

        case UsbBCablePurchaseItem.pId:
            return UsbBCablePurchaseItem.fromJson(json);

        case NfcFrontTapPurchaseItem.pId:
            return NfcFrontTapPurchaseItem.fromJson(json);

        default:
            throw new Error(`PurchaseItem json parse not implemented for productId ${json.productId}`);
    }
};

export class TerminalPurchaseItem implements PurchaseItem {
    readonly productId = TerminalPurchaseItem.pId;
    readonly unit = ItemUnit.pcs;

    static readonly regularPrice = 3850;
    static readonly pId = 1;

    public constructor(
        public amount: number,
        public locationId: number | null,
        public price: number,
        public subtext: string,
        public terminalModel: string = "FX 105",
    ) {}

    get name() {
        return `Kortleser (${this.terminalModel})`;
    }

    clone() {
        return new TerminalPurchaseItem(this.amount, this.locationId, this.price, this.subtext, this.terminalModel);
    }

    static fromJson(json: any): TerminalPurchaseItem {
        return new TerminalPurchaseItem(
            requireNum(json.amount),
            maybeNum(json.locationId),
            requireNum(json.price),
            maybeStr(json.subtext) || "",
            maybeStr(json.terminalModel) || "FX 105",
        )
    }
}

export class TerminalSignPurchaseItem implements PurchaseItem {
    readonly productId = TerminalSignPurchaseItem.pId;
    readonly name = "Kortleserskilt";
    readonly unit = ItemUnit.pcs;
    readonly subtext = "";

    static readonly regularPrice = 250;
    static readonly pId = 2;

    public constructor(
        public amount: number,
        public price: number,
    ) {}

    clone() {
        return new TerminalSignPurchaseItem(this.amount, this.price);
    }

    static fromJson(json: any): TerminalSignPurchaseItem {
        return new TerminalSignPurchaseItem(
            requireNum(json.amount),
            requireNum(json.price),
        )
    }
}

export class TerminalBatteryPurchaseItem implements PurchaseItem {
    readonly productId = TerminalBatteryPurchaseItem.pId;
    readonly unit = ItemUnit.pcs;
    readonly subtext = "";

    static readonly regularPrice = 300;
    static readonly pId = 3;

    public constructor(
        public amount: number,
        public price: number,
        public terminalModel: string = "FX 105",
    ) {}

    get name() {
        return `Batteri (${this.terminalModel})`;
    }

    clone(): PurchaseItem {
        return new TerminalBatteryPurchaseItem(this.amount, this.price, this.terminalModel);
    }

    static fromJson(json: any): TerminalBatteryPurchaseItem {
        return new TerminalBatteryPurchaseItem(
            requireNum(json.amount),
            requireNum(json.price),
            maybeStr(json.terminalModel) || "FX 105",
        );
    }

}

export class ChargerPurchaseItem implements PurchaseItem {
    readonly productId = ChargerPurchaseItem.pId;
    readonly name = "Ladeplugg";
    readonly unit = ItemUnit.pcs;
    readonly subtext = "";

    static readonly regularPrice = 150;
    static readonly pId = 4;

    public constructor(
        public amount: number,
        public price: number,
    ) {}

    clone(): PurchaseItem {
        return new ChargerPurchaseItem(this.amount, this.price);
    }

    static fromJson(json: any): ChargerPurchaseItem {
        return new ChargerPurchaseItem(
            requireNum(json.amount),
            requireNum(json.price),
        );
    }

}

export class UsbBCablePurchaseItem implements PurchaseItem {
    readonly productId = UsbBCablePurchaseItem.pId;
    readonly name = "Strømkabel (USB-C)";
    readonly unit = ItemUnit.pcs;
    readonly subtext = "";

    static readonly regularPrice = 100;
    static readonly pId = 5;

    public constructor(
        public amount: number,
        public price: number,
    ) {}

    clone(): PurchaseItem {
        return new UsbBCablePurchaseItem(this.amount, this.price);
    }

    static fromJson(json: any): UsbBCablePurchaseItem {
        return new UsbBCablePurchaseItem(
            requireNum(json.amount),
            requireNum(json.price),
        );
    }
}

export class NfcFrontTapPurchaseItem implements PurchaseItem {
    readonly productId = NfcFrontTapPurchaseItem.pId;
    readonly name = "Kortleser-plate (FX 105)";
    readonly unit = ItemUnit.pcs;
    readonly subtext = "";

    static readonly regularPrice = 400;
    static readonly pId = 6;

    public constructor(
        public amount: number,
        public price: number,
    ) {}

    clone(): PurchaseItem {
        return new NfcFrontTapPurchaseItem(this.amount, this.price);
    }

    static fromJson(json: any): NfcFrontTapPurchaseItem {
        return new NfcFrontTapPurchaseItem(
            requireNum(json.amount),
            requireNum(json.price),
        );
    }

}

export enum PurchaseOrderStatus {
    open = 1,
    confirmed = 100,
    crmRegistered = 101,
}

export class PurchaseOrder {
    public comment: string = "";
    public status: PurchaseOrderStatus = PurchaseOrderStatus.open;

    constructor(
        public id: number,
        public orderedByOrgnum: number,
        public orderedByPersonGuid: string,
        public orderedByPersonName: string,
        public orderedTs:DateTime,
        public deliveryAddress: DeliveryAddress,
        public items: PurchaseItem[],
    ) {}

    get orderSum(): number {
        if(this.items.length > 0) {
            const itemSums = this.items.map(it => it.amount * it.price);
            return itemSums.reduce((prev, curr, idx) => prev + curr);
        }
        return 0;
    }

    get isOpen() {
        return this.status === PurchaseOrderStatus.open;
    }

    statusTxt() {
        switch (this.status) {
            case PurchaseOrderStatus.open: return `Åpen`;
            case PurchaseOrderStatus.confirmed: return `Bekreftet (låst for redigering)`;
            case PurchaseOrderStatus.crmRegistered: return `Bekreftet (låst for redigering)`;
        }
    }

    static fromJson(json: any) {
        const da = DeliveryAddress.fromJson(json.deliveryAddress);
        const items: PurchaseItem[] = json.items instanceof Array ? (json.items as any[]).map(i => purchaseItemFromJson(i)) : [];
        const po = new PurchaseOrder(
            requireNum(json.id),
            requireNum(json.orderedByOrgnum),
            requireStr(json.orderedByPersonGuid),
            requireStr(json.orderedByPersonName),
            DateTime.fromJson(json.orderedTs),
            da,
            items
        );
        po.comment = maybeStr(json.comment) || "";
        po.status = maybeNum(json.status) || PurchaseOrderStatus.open;
        return po;
    }
}

export class RegReminderConfig {
    constructor(public enabled: boolean, public statusCode: number) {}

    static fromJson(json: any) {
        return new RegReminderConfig(
            json.enabled === true,
            requireNum(json.statusCode),
        )
    }

    static default() {
        return new RegReminderConfig(false, GeofenceStatusCode.Pending);
    }

    equal(other: RegReminderConfig): boolean {
        return this.enabled == other.enabled && this.statusCode == other.statusCode;
    }

    get errorMessage(): string | null {
        switch (this.statusCode) {
            case GeofenceStatusCode.Pending:
                return null;
            case GeofenceStatusCode.MissingPermission:
                return `App mangler tillatelse til posisjon`;
            case GeofenceStatusCode.Success:
                return null;
            case GeofenceStatusCode.SignInRequired:
                return `Google Play Services sign in påkrevd.`;
            case GeofenceStatusCode.Timeout:
                return `Geofence aktivering timeout.`;
            case GeofenceStatusCode.MissingBackgroundPermission:
                return `App mangler tillatelse for bakgrunnsposisjon.`;
            case GeofenceStatusCode.TooMany:
                return `For mange posisjonsområder registrert. Maks er 100 overvåkede områder.`;
            case GeofenceStatusCode.TooManyPendingIntents:
                return `For mange ventende handlinger registrert`;
            default:
                return `Feilkode returnert fra geofence teneste: ${this.statusCode}`;
        }
    }
}

export enum GeofenceStatusCode {
    MissingPermission = -101, // Self declared
    Pending = -100, // Self declared
    Success = 0,
    SignInRequired = 4,
    Timeout = 15,
    MissingBackgroundPermission = 1000,
    TooMany = 1001,
    TooManyPendingIntents = 1002,
}


export class PersonRegreminderLocation {
    constructor(public terminalLocationId: number,
                public terminalLocationDescription: string,
                public terminalLocationAddress: string | null,
                public terminalLocationConstructorName : string | null,
                public companyOrgnum: number,
                public companyName: string,
                public lastReg: DateTime,
                public geofenceActive: boolean,
                public regreminderAvailable: boolean,
                public geofenceCount: number)  {}

    static fromJson(json: any) {
        return new PersonRegreminderLocation(
            requireNum(json.terminalLocationId),
            requireStr(json.terminalLocationDescription),
            maybeStr(json.terminalLocationAddress),
            maybeStr(json.terminalLocationConstructorName),
            requireNum(json.companyOrgnum),
            requireStr(json.companyName),
            DateTime.fromJson(json.lastReg),
            json.geofenceActive,
            json.regreminderAvailable,
            json.geofenceCount,
        )
    }
}

export class MonthCountStat {
    constructor(public month: DateTime, public count: number) {}

    static fromJson(json: any) {
        return new MonthCountStat(
            DateTime.fromJson(json.month),
            requireNum(json.count)
        )
    }
}

export class NativeApiResponse {
    constructor(public payload: any) {}

    static fromJson(json: any) {
        if(typeof json.payload !== undefined) {
            return new NativeApiResponse(json.payload);
        } else {
            throw new Error(`NativeApiResponse must have a payload property`);
        }
    }
}

export class NotificationManagerStatus {
    static readonly GEOFENCING_CH_ID = "no.anleggsterminal.user.geofencing";

    constructor(public enabled: boolean, public channelsEnabled: Map<string, boolean>) {}

    toString(): string {
        const ch: string[] = [];
        const m = this.channelsEnabled.forEach((v, k) => ch.push(`${k}: ${v}`));
        return `NotificationManagerStatus: ${this.enabled} (${ch.join(", ")})`;
    }

    static fromJson(json: any) {
        let chE = new Map<string, boolean>();
        if(json.channelsEnabled && json.channelsEnabled.hasOwnProperty(this.GEOFENCING_CH_ID)) {
            chE.set(this.GEOFENCING_CH_ID, json.channelsEnabled[this.GEOFENCING_CH_ID] === true);
        }
        const ret = new NotificationManagerStatus(json.enabled === true, chE);
        return ret;
    }

    get regreminderNotificationsEnabled(): boolean {
        const chE = !this.channelsEnabled.has(NotificationManagerStatus.GEOFENCING_CH_ID) || this.channelsEnabled.get(NotificationManagerStatus.GEOFENCING_CH_ID) === true ;
        return this.enabled &&  chE ;
    }

    regreminderNotificationError(): string | null {
        if(!this.regreminderNotificationsEnabled) {
            return `Varsler for Registreringspåminnelse er avslått.`;
        } else {
            return null;
        }
    }
}

export class SetRegreminderResponse {
    constructor(public removedLocId: number | null, public removedLocDescription) {}

    static fromJson(json: any) {
        return new SetRegreminderResponse(numOrNull(json.removedLocId), nonEmptyString(json.removedLocDescription));
    }
}

export class Version {
    constructor(public major: number, public minor: number, public patch: number) {}

    toString() {
        return `${this.major}.${this.minor}.${this.patch}`;
    }

    lessThan(major: number, minor: number, patch: number) {
        return this.major < major ||
            (this.major == major && this.minor < minor) ||
            (this.major == major && this.minor == minor  && this.patch < patch);
    }
}

export class FamocoDevice {
    constructor(public id: number, public terminalGuid: string | null, public famocoId: string, public model: string, public imei: string[], public lastSync: DateTime | null,
                public maintenanceStatus: string, public syncStatus: string, public osVersion: string, public heartbeat: string | null,
                public createDate: DateTime | null, public updateDate: DateTime | null, public organizationId: string | null, public organizationName: string | null,
                public fleetId: string | null, public fleetName: string | null, public profileId : string | null, public profileName: string | null,
                public effectiveProfileId: string | null, public effectiveProfileName: string | null,
                public atAppVersionCode: number | null, public atAppVersion: string | null, public fetched: DateTime | null,
                public wifiEnabled: boolean, public mobileDataEnabled: boolean, public nfc: boolean) {}

    static fromJson(json: any) {
        const imei = json.imei instanceof Array ? json.imei as string[] : [];
        return new FamocoDevice(
            requireNum(json.id), maybeStr(json.terminal_guid), requireStr(json.famoco_id), requireStr(json.model), imei, DateTime.maybeFromJson(json.last_sync),
            requireStr(json.maintenance_status), requireStr(json.sync_status), requireStr(json.os_version), maybeStr(json.hearbeat),
            DateTime.maybeFromJson(json.create_date), DateTime.maybeFromJson(json.update_date), maybeStr(json.organization_id), maybeStr(json.organization_name),
            maybeStr(json.fleet_id), maybeStr(json.fleet_name), maybeStr(json.profile_id), maybeStr(json.profile_name),
            maybeStr(json.effective_profile_id), maybeStr(json.effective_profile_name), maybeNum(json.at_app_vc), maybeStr(json.at_app_v), DateTime.maybeFromJson(json.fetched_ts),
            json.wifi_enabled === true, json.data_enabled === true, json.nfc === true
        )
    }

    // Get the first imei in array, trimmed to remove last digit since that is the way we store it in AT
    get trimmedImei1() {
        let imei1 = this.fullImei1;
        if(imei1) {
            imei1 = imei1.substr(0, imei1.length - 1);
        }
        return imei1;
    }

    get fullImei1() {
        return this.imei.length > 0 ? this.imei[0] : null;
    }

    get firmwareVersion(): Version {
        let v = new Version(0, 0, 0);
        const m = this.osVersion.match(/_v(\d+)\.(\d+)\.(\d+)/);
        if(m) {
            v.major = requireNum(m[1]);
            v.minor = requireNum(m[2]);
            v.patch = requireNum(m[3]);
        }
        return v;
    }

    get hasSetupDeviation() {
        return this.maintenanceStatus != "In the field" ||
            this.atAppVersionCode && this.atAppVersionCode < 90 ||
            !this.mobileDataEnabled ||
            !this.nfc;
    }

    get isSynced() {
        return this.syncStatus === "Synced";
    }

    // Determine if the famoco data should be refetched from famoco server api
    // Returns true when data is more than a minute old
    shouldRefresh() {
        return this.fetched && this.fetched.plusMillis(60_000).isBefore(DateTime.now());
    }
}

export class PendingEmailConfirmInfo {
    constructor(public name: String, public email: String) {}

    static fromJson(json: any): PendingEmailConfirmInfo {
        return new PendingEmailConfirmInfo(requireStr(json.name), requireStr(json.email));
    }
}

export class HmsregLocationStatus {
    constructor(public terminalLocationId: number, public terminalLocationDescr: string, public hmsregNumber: number,
                public lastTransferErrorCode: number | null, public queuedCount: number) {
    }

    static fromJson(json: any) {
        return new HmsregLocationStatus(
            requireNum(json.terminalLocationId),
            requireStr(json.terminalLocationDescr),
            requireNum(json.hmsregNumber),
            numOrNull(json.lastTransferErrorCode),
            requireNum(json.queuedCount),
        );
    }

    transferErrorMsg(): string {
        switch (this.lastTransferErrorCode) {
            case 401:
            case 403:
                return `Autentiseringsfeil. Kontroller/korriger pålogging`
            default:
                return `Overføringsfeil. Kontakt Proresult for feilsøking`
        }
    }

}

export class HseGuideline {
    constructor(
        public id: number, public terminalLocationId: number, public title: string, public version: number, public contentUrl: string | null,
        public insertTs: DateTime, public updateTs: DateTime
    ) {}

    static fromJson(json: any): HseGuideline {
        return new HseGuideline(
            requireNum(json.id), requireNum(json.terminalLocationId), requireStr(json.title), requireNum(json.version),
            maybeStr(json.contentUrl), DateTime.fromJson(json.insertTs),
            DateTime.fromJson(json.updateTs)
        )
    }
}

// Matches model class in (android) app, BackgroundLocationPermission.kt
export class BackgroundLocationPermission {
    constructor(public background: boolean, public permissionStatus: number) {}

    /**
     * @return true if app is allowed to directly request permission
     */
    askAllowed(): boolean {
        return this.permissionStatus < 2 // 2 = STATUS_DENIED in android app code
    }

    static fromJson(json: any): BackgroundLocationPermission {
        return new BackgroundLocationPermission(json.background === true, requireNum(json.permissionStatus));
    }
}

export interface OutOfStockShopItem {
    name: string;
    action: string;
}

export interface AddressSearchResultItem {
    address: string;
    lat: number;
    lng: number;
}

export interface SearchedMarker {
    lat: number;
    lng: number;
}
